// Available Plans list: which Plan State filter to open on, and what the
// empty row says (DEV-319 / BUG-007). The list used to open on "Available"
// no matter what, so a client whose two plans start next month showed
// "Available (0) · No plans available · Add First Plan" and read as if the
// plans had been deleted. Pure decisions, unit tested.

export type PlanStateFilter = "all" | "available" | "closed" | "future";

export interface PlanStateCounts {
  available?: number;
  closed?: number;
  future?: number;
}

const STATE_LABEL: Record<Exclude<PlanStateFilter, "all">, string> = {
  available: "Available",
  closed: "Closed",
  future: "Future",
};

const n = (v: number | undefined) => (Number.isFinite(v) ? Math.max(0, Number(v)) : 0);

export function totalPlanCount(counts: PlanStateCounts): number {
  return n(counts.available) + n(counts.closed) + n(counts.future);
}

export interface DefaultPlanStateFilterOptions {
  /** A filter the user picked (or a flow set, e.g. after a renewal) — always wins. */
  userChoice?: PlanStateFilter | null;
  /** Arrived with `?newPlans=` — the plans just created are pointed at; show everything. */
  arrivedWithNewPlans?: boolean;
}

/**
 * Filter the list opens on. "Available" when it has plans; when Available is
 * empty, "Future" if that is the only state with plans, otherwise "All". A
 * user's pick and the ?newPlans landing are never overridden. With no plans
 * at all, "Available" (the empty row then offers "Add First Plan").
 */
export function defaultPlanStateFilter(
  counts: PlanStateCounts,
  opts: DefaultPlanStateFilterOptions = {},
): PlanStateFilter {
  if (opts.userChoice) return opts.userChoice;
  if (opts.arrivedWithNewPlans) return "all";
  if (n(counts.available) > 0) return "available";
  const closed = n(counts.closed);
  const future = n(counts.future);
  if (future > 0 && closed === 0) return "future";
  if (future > 0 || closed > 0) return "all";
  return "available";
}

export type PlanListEmptyState =
  /** Plan list still loading — show neither "Add First Plan" nor a hint. */
  | { kind: "loading" }
  /** The filter has rows — no empty state. */
  | { kind: "none" }
  /** The client has no plans at all — offer "Add First Plan". */
  | { kind: "no-plans" }
  /** This state is empty but others have plans — point at them, offer "Show all". */
  | { kind: "filtered-out"; message: string; hiddenCount: number };

/** "2 Future plans" / "1 Available plan, 3 Closed plans" — states other than `filter` that have plans. */
export function otherStatesSummary(counts: PlanStateCounts, filter: PlanStateFilter): string {
  return (["available", "future", "closed"] as const)
    .filter((state) => state !== filter && n(counts[state]) > 0)
    .map((state) => `${n(counts[state])} ${STATE_LABEL[state]} plan${n(counts[state]) === 1 ? "" : "s"}`)
    .join(", ");
}

export function planListEmptyState(input: {
  counts: PlanStateCounts;
  filter: PlanStateFilter;
  /** Rows the current filter shows. */
  visibleCount: number;
  loading?: boolean;
}): PlanListEmptyState {
  if (input.loading) return { kind: "loading" };
  if (input.visibleCount > 0) return { kind: "none" };
  const total = totalPlanCount(input.counts);
  if (total === 0) return { kind: "no-plans" };
  return {
    kind: "filtered-out",
    message: `No plans in this state · ${otherStatesSummary(input.counts, input.filter)}`,
    hiddenCount: total,
  };
}
